import { useState, useCallback, useEffect, useMemo } from 'react';
import { startOfMonth, endOfMonth } from 'date-fns';

// Helper function for deep merging of objects
const deepMerge = (target, source) => {
  const output = { ...target };
  
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      // Special handling for 'drilldown' property - don't inherit from parent
      if (key === 'drilldown') {
        // Only use source's drilldown if it exists, otherwise set to undefined
        // This ensures we don't carry forward parent's drilldown when at the bottom level
        output[key] = source[key];
      } else if (isObject(source[key])) {
        if (!(key in target)) {
          output[key] = source[key];
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        output[key] = source[key];
      }
    });
  }
  
  return output;
};

// Helper to check if value is an object
const isObject = (item) => {
  return (item && typeof item === 'object' && !Array.isArray(item));
};

export const useDrilldown = (initialConfig) => {
  const [drilldownConfig, setDrilldownConfig] = useState(null);
  const [drilldownParams, setDrilldownParams] = useState({});
  const [drilldownHistory, setDrilldownHistory] = useState([]);
  const [drilldownData, setDrilldownData] = useState(null);
  const [seriesCache, setSeriesCache] = useState(null);
  const [navigationPath, setNavigationPath] = useState([]);
  const [selectedChartType, setSelectedChartType] = useState(null);
  const [isDrilldownDisabled, setIsDrilldownDisabled] = useState(false);

  useEffect(() => {
    if (typeof initialConfig?.series === 'function') {
      const loadSeries = async () => {
        try {
          const result = await initialConfig.series();
          if (Array.isArray(result)) {
            setSeriesCache(result);
          }
        } catch (error) {
          // Silent error handling
        }
      };
      
      loadSeries();
    } else if (Array.isArray(initialConfig?.series)) {
      setSeriesCache(initialConfig.series);
    }
  }, [initialConfig]);

  const resolveLabel = useCallback((labelValue, dataPoint = null, parentDataPoints = [], fallback = '') => {
    if (typeof labelValue === 'function') {
      return labelValue(dataPoint, parentDataPoints);
    }
    return labelValue || fallback;
  }, []);

  useEffect(() => {
    let path = [];
    
    const activeConfig = drilldownConfig || initialConfig;
    
    // Build navigation path from history
    const parentDataPoints = [];
    
    drilldownHistory.forEach((historyItem, index) => {
      // Use the drilldownLabel function from config for historical items
      const labelFunc = historyItem.config.drilldownLabel;
      let label = 'Item';
      
      if (labelFunc) {
        // Pass parent dataPoints to the drilldownLabel function
        label = resolveLabel(labelFunc, historyItem.dataPoint, parentDataPoints.slice());
      } else if (historyItem.dataPoint) {
        // Fallback to dataPoint properties if no drilldownLabel provided
        label = historyItem.dataPoint.name || historyItem.dataPoint.label || 'Item';
      }
      
      path.push({
        label: label,
        level: index
      });
      
      // Add this item to the parentDataPoints array for the next level
      if (historyItem.dataPoint) {
        parentDataPoints.push(historyItem.dataPoint);
      }
    });
    
    // Add the current level to the path
    // Use the drilldownLabel function without a dataPoint for current level
    const currentLabelFunc = activeConfig?.drilldownLabel;
    let currentLabel = activeConfig?.title || 'Overview';
    
    if (currentLabelFunc) {
      currentLabel = resolveLabel(currentLabelFunc, null, parentDataPoints);
    }
    
    path.push({
      label: currentLabel
    });
    
    setNavigationPath(path);
    
    // Reset the drilldown disabled state when navigating to a new level
    setIsDrilldownDisabled(false);
    
    // Check if we should disable drilldown for this level
    if (activeConfig?.disableDrilldown) {
      const shouldDisable = activeConfig.disableDrilldown(parentDataPoints);
      setIsDrilldownDisabled(shouldDisable);
    }
  }, [drilldownConfig, drilldownHistory, initialConfig, resolveLabel, drilldownData]);

  const navigateToLevel = useCallback((level) => {
    if (level < 0 || level > drilldownHistory.length) return;
    
    // Always reset the drilldown disabled flag when navigating
    setIsDrilldownDisabled(false);
    
    if (level === 0) {
      // Clear history but maintain consistent state management pattern
      setDrilldownHistory([]);
      setDrilldownConfig(null);
      setDrilldownParams({});
      setDrilldownData(null);
      return;
    }
    
    if (level >= drilldownHistory.length) return;
    
    const newHistory = drilldownHistory.slice(0, level);
    const lastHistoryItem = newHistory[newHistory.length - 1];
    
    setDrilldownHistory(newHistory);
    setDrilldownConfig(lastHistoryItem.config.drilldown);
    setDrilldownParams(lastHistoryItem.params);
  }, [drilldownHistory]);

  const handleDrilldown = useCallback((dataPoint) => {
    // Skip drilldown if it's been disabled for the current level
    if (isDrilldownDisabled) {
      console.log('Drilldown is disabled for the current level');
      return;
    }
    
    
    const currentConfig = drilldownConfig || initialConfig;
    
    if (!currentConfig?.drilldown) return;
    
    setDrilldownData(dataPoint);

    const additionalParams = {};
    
    // Directly capture important parameters from the dataPoint
    if (dataPoint.modelVersionZoneId) {
      additionalParams.modelVersionZoneId = dataPoint.modelVersionZoneId;
    }
    
    if (dataPoint.year && dataPoint.month) {
      try {
        const monthDate = new Date(dataPoint.year, dataPoint.month - 1);
        const startDate = startOfMonth(monthDate);
        const endDate = endOfMonth(monthDate);
        
        additionalParams.startDate = startDate.toISOString();
        additionalParams.endDate = endDate.toISOString();
      } catch (error) {
        // Silent error handling
      }
    }
    
    if (currentConfig?.drilldown?.getParams) {
      const customParams = currentConfig.drilldown.getParams(dataPoint, drilldownParams);
      Object.assign(additionalParams, customParams);
    }

    const newParams = {
      ...drilldownParams,
      ...additionalParams
    };
    
    const newHistory = [...drilldownHistory, { 
      config: currentConfig,
      params: newParams,
      dataPoint
    }];
    
    setDrilldownHistory(newHistory);
    setDrilldownParams(newParams);
    setDrilldownConfig(currentConfig.drilldown);
  }, [initialConfig, drilldownConfig, drilldownParams, drilldownHistory]);

  // Create a merged config that uses initialConfig as base and sequentially applies each drilldown config
  const activeConfig = useMemo(() => {
    // Start with the initial config, whether we're at level 0 or not
    let builtConfig = { ...initialConfig };
    
    // Only apply drilldown configs if we've drilled down
    if (drilldownConfig && drilldownHistory.length > 0) {
      // Get the last history item, which represents the current level
      const currentLevelHistoryItem = drilldownHistory[drilldownHistory.length - 1];
      
      // Merge all the configs up to the current level
      for (let i = 0; i < drilldownHistory.length; i++) {
        const historyItem = drilldownHistory[i];
        if (historyItem.config.drilldown) {
          // For each level, apply the drilldown config on top of the current built config
          builtConfig = deepMerge(builtConfig, historyItem.config.drilldown);
        }
      }
      
      // If we're at the bottom level (current drilldown has no further drilldown),
      // explicitly set drilldown to undefined
      if (!currentLevelHistoryItem.config.drilldown.drilldown) {
        builtConfig.drilldown = undefined;
      }
      
      // Ensure itemNavigation is properly passed through
      if (currentLevelHistoryItem.config.drilldown.itemNavigation) {
        builtConfig.itemNavigation = currentLevelHistoryItem.config.drilldown.itemNavigation;
      }
    }

    // Always run chart type validation, regardless of level
    if (selectedChartType && builtConfig.allowedChartTypes && 
        !builtConfig.allowedChartTypes.includes(selectedChartType)) {
      setSelectedChartType(builtConfig.defaultChartType);
    }
    
    return builtConfig;
  }, [drilldownConfig, initialConfig, drilldownHistory, selectedChartType]);

  // Define resetDrilldown to call navigateToLevel(0)
  const resetDrilldown = useCallback(() => {
    navigateToLevel(0);
  }, [navigateToLevel]);

  return {
    config: activeConfig,
    originalConfig: initialConfig,
    drilldownParams,
    isDrilledDown: !!drilldownConfig,
    drilldownHistory,
    navigationPath,
    handleDrilldown,
    navigateToLevel,
    resetDrilldown,
    drilldownData,
    seriesCache,
    selectedChartType,
    setSelectedChartType,
    isDrilldownDisabled,
    isDrilldownEnabled: () => {
      const enabled = (activeConfig?.drilldown || activeConfig?.itemNavigation) && !isDrilldownDisabled;
      console.log('isDrilldownEnabled check:', {
        activeConfig: activeConfig ? {
          id: activeConfig.id,
          hasItemNavigation: !!activeConfig.itemNavigation,
          hasDrilldown: !!activeConfig.drilldown
        } : null,
        isDrilldownDisabled,
        result: enabled
      });
      return enabled;
    }
  };
};
